// frontend/src/components/calendar/CalendarView.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs';
import {
    Box,
    Paper,
    Skeleton,
    Container,
    Snackbar,
    Alert,
    Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { scheduledPostsApi } from '../../services/api';
import CalendarToolbar from './CalendarToolbar';
import CalendarEvent from './CalendarEvent';
import PostDetailDrawer from './PostDetailDrawer';
import UpcomingSidebar from './UpcomingSidebar';


const localizer = dayjsLocalizer(dayjs);

const CalendarView = () => {
    const navigate = useNavigate();
    const [view, setView] = useState('month');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [activePlatforms, setActivePlatforms] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchEvents = useCallback(async (start, end) => {
        setLoading(true);
        setError(null);
        try {
            const startIso = dayjs(start).startOf('day').toISOString();
            const endIso = dayjs(end).endOf('day').toISOString();
            const data = await scheduledPostsApi.getRange(startIso, endIso);

            const normalizedEvents = (data || []).map(post => ({
                id: post.id,
                title: (post.platforms && post.platforms[0]) || 'Scheduled Post',
                start: new Date(post.scheduledFor),
                end: new Date(post.scheduledFor),
                resource: post
            }));
            setEvents(normalizedEvents);
        } catch (err) {
            setError(err.message || 'Failed to load scheduled posts. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const start = dayjs(currentDate).startOf(view === 'month' ? 'month' : 'week').subtract(1, 'week').toDate();
        const end = dayjs(currentDate).endOf(view === 'month' ? 'month' : 'week').add(1, 'week').toDate();
        fetchEvents(start, end);
    }, [currentDate, view, fetchEvents, retryCount]);

    const handleRetry = () => {
        setRetryCount(prev => prev + 1);
    };

    const filteredEvents = useMemo(() => {
        if (activePlatforms.length === 0) return events;
        return events.filter(event =>
            event.resource.platforms.some(p => activePlatforms.includes(p))
        );
    }, [events, activePlatforms]);

    const handlePlatformToggle = (platform) => {
        if (platform === null) {
            setActivePlatforms([]);
        } else {
            setActivePlatforms(prev =>
                prev.includes(platform)
                    ? prev.filter(p => p !== platform)
                    : [...prev, platform]
            );
        }
    };

    const handleSlotSelect = ({ start }) => {
        navigate('/app/post', {
            state: {
                scheduledDate: start.toISOString(),
                initialPlatforms: activePlatforms.length > 0 ? activePlatforms : null
            }
        });
    };

    const handleEventSelect = (event) => {
        setSelectedEvent(event.resource);
        setDrawerOpen(true);
    };

    const handleCancelled = (postId) => {
        setEvents(prev => prev.filter(e => e.id !== postId));
        setDrawerOpen(false);
        setSnackbar({ open: true, message: 'Post cancelled successfully', severity: 'success' });
    };

    const renderCalendarBody = () => {
        // Initial loading — no events yet
        if (loading && events.length === 0) {
            return (
                <Box sx={{ p: 2 }}>
                    <Skeleton variant="rectangular" height={600} sx={{ borderRadius: 2 }} />
                </Box>
            );
        }

        // First load failed with no cached events — show full error state
        if (error && events.length === 0) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 500,
                        gap: 2,
                        p: 4,
                    }}
                >
                    <Alert
                        severity="error"
                        sx={{ width: '100%', maxWidth: 520, borderRadius: 3 }}
                    >
                        {error}
                    </Alert>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRetry}
                        sx={{ borderRadius: 2 }}
                    >
                        Try Again
                    </Button>
                </Box>
            );
        }

        return (
            <>
                {/* Inline banner when a navigation refresh fails but cached events are visible */}
                {error && (
                    <Alert
                        severity="error"
                        onClose={() => setError(null)}
                        action={
                            <Button color="inherit" size="small" onClick={handleRetry}>
                                Retry
                            </Button>
                        }
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        {error}
                    </Alert>
                )}
                <Calendar
                    localizer={localizer}
                    events={filteredEvents}
                    view={view}
                    date={currentDate}
                    onNavigate={setCurrentDate}
                    onView={setView}
                    onSelectSlot={handleSlotSelect}
                    onSelectEvent={handleEventSelect}
                    selectable
                    components={{
                        toolbar: (props) => (
                            <CalendarToolbar
                                {...props}
                                activePlatforms={activePlatforms}
                                onPlatformToggle={handlePlatformToggle}
                            />
                        ),
                        event: CalendarEvent,
                    }}
                    style={{ height: 'calc(100vh - 200px)', minHeight: 650 }}
                />
            </>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Paper sx={{ p: 3, borderRadius: 4, bgcolor: 'background.paper', overflow: 'hidden' }}>
                        {renderCalendarBody()}
                    </Paper>
                </Box>

                <Box sx={{ width: 280, flexShrink: 0, display: { xs: 'none', lg: 'block' } }}>
                    <Paper sx={{ p: 3, borderRadius: 4, height: 'fit-content' }}>
                        <UpcomingSidebar
                            events={events}
                            onEventClick={(resource) => {
                                setSelectedEvent(resource);
                                setDrawerOpen(true);
                            }}
                        />
                    </Paper>
                </Box>
            </Box>

            <PostDetailDrawer
                open={drawerOpen}
                post={selectedEvent}
                onClose={() => setDrawerOpen(false)}
                onCancelled={handleCancelled}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CalendarView;
